import { Plus } from 'lucide-react'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Tag } from '@/components/Tag'
import type { DocumentRequirements } from '@/types/bundle'
import { documentTypeLabel } from '../labels'

export interface DocumentRequirementsSectionProps {
  documentRequirements: DocumentRequirements
  onChange: (documentRequirements: DocumentRequirements) => void
  onAddDocumentType: () => void
}

// hard_floor, must_have_all, and must_have_any_of each render via genuinely
// different branches (PRD 02 AC3): hard_floor and must_have_all are their own
// flat rows of solid Tags, each must_have_any_of group gets its own
// dashed-border cluster. Confirmed with the user: hard_floor is exposed as
// its own independently-editable list, even though request_validator.py
// currently checks it exactly the same way as must_have_all (a union) - the
// two fields genuinely exist separately in the schema, and the admin should
// be able to author them separately rather than have one silently overwrite
// the other on every save.
export function DocumentRequirementsSection({
  documentRequirements,
  onChange,
  onAddDocumentType,
}: DocumentRequirementsSectionProps) {
  const rules = documentRequirements.rules ?? {}
  const hardFloor = documentRequirements.hard_floor ?? []
  const mustHaveAll = rules.must_have_all ?? []
  const mustHaveAnyOf = rules.must_have_any_of ?? []

  function removeFromHardFloor(key: string) {
    onChange({ ...documentRequirements, hard_floor: hardFloor.filter((k) => k !== key) })
  }

  function removeFromMustHaveAll(key: string) {
    onChange({
      ...documentRequirements,
      rules: { ...rules, must_have_all: mustHaveAll.filter((k) => k !== key) },
    })
  }

  function removeFromAnyOfGroup(groupIndex: number, key: string) {
    onChange({
      ...documentRequirements,
      rules: {
        ...rules,
        must_have_any_of: mustHaveAnyOf.map((group, index) =>
          index === groupIndex ? group.filter((k) => k !== key) : group,
        ),
      },
    })
  }

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Document requirements
          <InfoTooltip text="Which document types must be uploaded for a request to this product to be accepted." />
        </h2>
        <button
          type="button"
          onClick={onAddDocumentType}
          className="flex items-center gap-1 rounded-control border border-border px-3 py-1.5 text-meta font-medium text-text-primary hover:border-accent/60"
        >
          <Plus size={14} strokeWidth={1.5} />
          Add Document Type
        </button>
      </div>

      <p className="text-meta text-text-secondary">
        A request must include every document type in "Hard floor" and "Must have all". For "Any
        of" groups, at least one document type from each group must be present.
      </p>

      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
          Hard floor
          <InfoTooltip text="A second, independent list of required document types. The platform checks it exactly the same way as 'Must have all' - there's currently no difference in behaviour between putting a document type here versus there." />
        </span>
        {hardFloor.length === 0 ? (
          <span className="text-meta text-text-disabled">No required document types yet</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {hardFloor.map((key) => (
              <Tag key={key} label={documentTypeLabel(key)} onRemove={() => removeFromHardFloor(key)} />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
          Must have all
          <InfoTooltip text="Checked exactly the same way as 'Hard floor' above - there's currently no difference in behaviour between putting a document type here versus there." />
        </span>
        {mustHaveAll.length === 0 ? (
          <span className="text-meta text-text-disabled">No required document types yet</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {mustHaveAll.map((key) => (
              <Tag key={key} label={documentTypeLabel(key)} onRemove={() => removeFromMustHaveAll(key)} />
            ))}
          </div>
        )}
      </div>

      {mustHaveAnyOf.map((group, index) => (
        <div
          key={index}
          className="flex flex-col gap-2 rounded-control border border-dashed border-border p-3"
        >
          <span className="text-meta font-medium text-text-secondary">Any of - group {index + 1}</span>
          <div className="flex flex-wrap gap-2">
            {group.map((key) => (
              <Tag
                key={key}
                label={documentTypeLabel(key)}
                onRemove={() => removeFromAnyOfGroup(index, key)}
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  )
}
